import {
  Badge,
  Button,
  type ColumnDef,
  DataGrid,
  DataGridColumnHeader,
  Label,
  type PaginationState,
  WithToolTip,
} from "@atlas/core";
import { useTranslation } from "@atlas/sdk";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useMemo } from "react";
import { SubscriptionExpandedRow } from "./SubscriptionExpandedRow";
import { truncatingHeaderTitle } from "./truncatingHeaderTitle";
import type { ISubscriptionRow } from "./types";

const DATA_GRID_LAYOUT = {
  columnsVisibility: true,
  headerBackground: true,
  tableBorder: true,
  height: "full", // Added to ensure scrollability in the tab content
  headerSticky: true,
} as const;

interface ISubscriptionTabProps {
  data: ISubscriptionRow[];
  totalElements?: number;
  totalPages?: number;
  pagination: PaginationState;
  onPaginationChange: (
    updater: PaginationState | ((old: PaginationState) => PaginationState),
  ) => void;
  loading?: boolean;
}

export function SubscriptionTab({
  data: subscriptionData,
  totalElements = 0,
  totalPages = 0,
  pagination,
  onPaginationChange,
  loading = false,
}: ISubscriptionTabProps) {
  const { t } = useTranslation();

  // Memoized filter options to prevent unnecessary re-renders of the filter dropdown
  const statusFilterOptions = useMemo(
    () => [
      { label: t("hub.profile.tenant_active"), value: "Active" },
      { label: t("hub.profile.tenant_inactive"), value: "Inactive" },
    ],
    [t],
  );

  const subscriptionColumns = useMemo<ColumnDef<ISubscriptionRow, any>[]>(
    () => [
      {
        id: "expand",
        header: () => null,
        size: 28,
        minSize: 20,
        cell: ({ row }) => {
          return (
            <Button
              variant="ghost"
              size="icon"
              className="size-8"
              onClick={(e) => {
                e.stopPropagation();
                row.toggleExpanded();
              }}>
              {row.getIsExpanded() ? (
                <ChevronDown className="size-4" />
              ) : (
                <ChevronRight className="size-4" />
              )}
            </Button>
          );
        },
        meta: {
          expandedContent: (subscription) => (
            <SubscriptionExpandedRow row={subscription} />
          ),
        },
      },
      {
        accessorKey: "subscribedTo",
        header: (info) => (
          <DataGridColumnHeader
            column={info.column}
            title={t("hub.subscription.subscribed_to")}
          />
        ),
        cell: (info) => (
          <Label className="text-foreground">
            {(info.getValue() as string) ?? "\u2014"}
          </Label>
        ),
      },
      {
        accessorKey: "softwareStartDate",
        header: (info) => (
          <DataGridColumnHeader
            column={info.column}
            title={t("hub.subscription.software_start_date")}
          />
        ),
        cell: (info) => {
          const val = info.getValue() as string;
          return val ? val.split("T")[0] : "\u2014";
        },
      },
      {
        accessorKey: "softwareEndDate",
        header: (info) => (
          <DataGridColumnHeader
            column={info.column}
            title={t("hub.subscription.software_end_date")}
          />
        ),
        cell: (info) => {
          const val = info.getValue() as string;
          return val ? val.split("T")[0] : "\u2014";
        },
      },
      {
        accessorKey: "subscriptionSourceBillCode",
        minSize: 160,
        header: (info) => (
          <DataGridColumnHeader
            column={info.column}
            className="min-w-0 max-w-full gap-1"
            title={truncatingHeaderTitle(
              t("hub.subscription.subscription_source_bill_code"),
            )}
          />
        ),
        cell: (info) => (
          <Label className="text-foreground">
            {(info.getValue() as string) ?? "\u2014"}
          </Label>
        ),
      },
      {
        accessorKey: "subscriptionInvoiceBillCode",
        minSize: 160,
        header: (info) => (
          <DataGridColumnHeader
            column={info.column}
            className="min-w-0 max-w-full gap-1"
            title={truncatingHeaderTitle(
              t("hub.subscription.subscription_invoice_bill_code"),
            )}
          />
        ),
        cell: (info) => (
          <Label className="text-foreground">
            {(info.getValue() as string) ?? "\u2014"}
          </Label>
        ),
      },
      {
        accessorKey: "createdOn",
        header: (info) => (
          <DataGridColumnHeader
            column={info.column}
            title={t("hub.subscription.created_on")}
          />
        ),
        cell: (info) => {
          const val = info.getValue() as string;
          return val ? val.split("T")[0] : "\u2014";
        },
      },
      {
        accessorKey: "status",
        header: (info) => (
          <DataGridColumnHeader
            column={info.column}
            title={t("hub.administer.status")}
          />
        ),
        filterFn: (row, columnId, filterValue) => {
          const values = Array.isArray(filterValue) ? filterValue : [];
          if (values.length === 0) return true;
          return values.some(
            (val) =>
              String(row.getValue(columnId)).toLowerCase() ===
              String(val).toLowerCase(),
          );
        },
        cell: (info) => {
          const status = (info.getValue() as string) ?? "";
          const isActive = status.toLowerCase() === "active";
          return (
            <Badge
              variant={isActive ? "success" : "neutral"}
              appearance="ghost"
              size="sm"
              dot>
              {status || "\u2014"}
            </Badge>
          );
        },
      },
      {
        accessorKey: "createdBy",
        minSize: 200,
        meta: { headerTitle: t("hub.catalog_details.created_by") },
        header: (info) => (
          <DataGridColumnHeader
            column={info.column}
            title={t("hub.catalog_details.created_by")}
          />
        ),
        cell: (info) => {
          const email = (info.getValue() as string) ?? "\u2014";
          return (
            <WithToolTip tooltipText={email}>
              <Label className="font-normal text-muted-foreground truncate block w-full">
                {email}
              </Label>
            </WithToolTip>
          );
        },
      },
    ],
    [t],
  );

  return (
    <div className="flex flex-1 flex-col min-h-0 h-full overflow-hidden">
      <DataGrid<ISubscriptionRow>
        data={subscriptionData}
        columns={subscriptionColumns}
        recordCount={totalElements}
        tableLayout={DATA_GRID_LAYOUT}
        withExport
        withSearch={{
          searchKey: "subscribedTo",
          placeholder: t("hub.subscription.search_results"),
        }}
        withFilter={{
          filters: [
            {
              columnId: "status",
              label: t("hub.administer.status"),
              options: statusFilterOptions,
            },
          ],
        }}
        paginationPlacement="toolbar"
        manualPagination
        pageCount={totalPages}
        pagination={pagination}
        onPaginationChange={onPaginationChange}
        loading={loading}
        loadingMessage={t("hub.subscription.loading")}
        emptyMessage={t("hub.subscription.no_subscription_created")}
        withExpandable
        className="flex-1 min-h-0 h-full"
      />
    </div>
  );
}
